<template>
    <div class="sandwich">
        <div
            class="block preview"
            @touchstart="onStart"
            @touchmove="onMove"
            @touchend="onEnd"
            :style="previewStyle"
        >
            <slot name="first-preview"></slot>
        </div>

        <div
            class="block detail"
            @touchstart="onStart"
            @touchmove="onMove"
            @touchend="onEnd"
            :style="detailStyle"
        >
            <slot name="first-detail"></slot>
        </div>

        <div class="block second" :style="secondStyle">
            <slot name="second"></slot>
        </div>
    </div>
</template>


<script setup>
import { reactive, computed } from "vue";

const props = defineProps({
    threshold: { type: Number, default: 120 },
    mindelta: { type: Number, default: 40 },
    dragging: { type: Boolean, default: true },
    secondaryHeight: { type: String, default: 'var(--nav-height)' }
});

const emit = defineEmits(['expand', 'collapse']);

const data = reactive({
    expanded: false,
    startY: 0,
    deltaY: 0,
    dragging: false
});

const onStart = (e) => {
    if (!props.dragging) {
        return
    }

    data.dragging = true;
    data.startY = e.touches[0].clientY;
};

const onMove = (e) => {
    if (!props.dragging) {
        return
    }

    if (!data.dragging) {
        return;
    }

    const y = e.touches[0].clientY;
    data.deltaY = y - data.startY;

    if (y > data.startY) {
        data.deltaY = Math.max(0, data.deltaY - props.mindelta);
    } else if (y < data.startY) {
        data.deltaY = Math.min(0, data.deltaY + props.mindelta);
    }

    if (!data.expanded && data.deltaY > 0) {
        data.deltaY = 0;
    }

    if (data.expanded && data.deltaY < 0) {
        data.deltaY = 0;
    }
};

const onEnd = () => {
    if (!props.dragging) {
        return
    }

    data.dragging = false;

    if (!data.expanded && data.deltaY < -props.threshold) {
        data.expanded = true;
        emit('expand');
    } else if (data.expanded && data.deltaY > props.threshold) {
        data.expanded = false;
        emit('collapse');
    }

    data.deltaY = 0;
};

const progress = computed(() => {
    const limit = 300;
    const dy = Math.min(Math.abs(data.deltaY), limit);
    return dy / limit;
});

const previewStyle = computed(() => ({
    transform: data.dragging && data.deltaY
        ? `translateY(${data.deltaY}px)`
        : data.expanded ? "translateY(-100vh)" : "translateY(0)",
    opacity: data.dragging && data.deltaY ? 1 - progress.value : data.expanded ? 0 : 1,
    transition: data.dragging && data.deltaY ? "none" : "0.35s ease"
}));

const detailStyle = computed(() => ({
    transform: data.dragging && data.deltaY
        ? `translateY(calc(${ !data.expanded ? '100%' : '0%'} + ${data.deltaY}px - ${data.expanded ? '0px' : props.secondaryHeight}))`
        : data.expanded ? "translateY(0)" : `translateY(calc(100vh - ${props.secondaryHeight}))`,
    opacity: !data.expanded && data.deltaY ? Math.max(0, Math.min(1, -data.deltaY / 40)) : 1,
    transition: data.dragging && data.deltaY ? "none" : "0.35s ease"
}));

const secondStyle = computed(() => ({
    transform: data.dragging
        ? (
            data.expanded ? `translateY(calc(100vh - ${data.deltaY}px))` : `translateY(${-data.deltaY}px)`
        )
        : data.expanded ? "translateY(100vh)" : "translateY(0)",
    opacity: data.dragging ? 1 - progress.value : data.expanded ? 0 : 1,
    transition: data.dragging ? "none" : "0.35s ease"
}));

const expand = () => {
    if (data.expanded) {
        return;
    }

    data.dragging = false;
    data.deltaY = 0;

    requestAnimationFrame(() => {
        data.expanded = true;
        emit('expand');
    });
};

const collapse = () => {
    if (!data.expanded) {
        return;
    }

    data.dragging = false;
    data.deltaY = 0;

    requestAnimationFrame(() => {
        data.expanded = false;
        emit('collapse');
    });
};

defineExpose({
    expand,
    collapse
});
</script>


<style scoped lang="scss">
.sandwich {
    position: relative;
    width: 100%;
    overflow: visible;

    &.footer {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 10;
        background: inherit;
    }
}

.block {
    width: 100%;
    will-change: transform, opacity;

    &.second {
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
    }
}

.preview {
    position: relative;
    z-index: 5;
}

.detail {
    position: fixed;
    width: 100%;
    height: 100vh;
    top: 0;
    left: 0;
    pointer-events: auto;
    z-index: 20;
}

.second {
    position: relative;
    z-index: 21;
}
</style>

